import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  Timestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import { useNavigate } from "react-router-dom";
import studydenHorizontal from "./assets/images/branding/Studyden_Horizontal.svg";
import "./TypingTest.css";

const TypingTest = ({ user }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wordContainerRef = useRef(null);
  const testDurationInSeconds = 30;

  const correctCharsRef = useRef(0);
  const incorrectCharsRef = useRef(0);
  const totalTypedCharsRef = useRef(0);
  const isTestCompletedRef = useRef(false);

  const wordList = [
    "the", "be", "to", "of", "and", "a", "in", "that", "have", "I",
    "it", "for", "not", "on", "with", "he", "as", "you", "do", "at",
    "this", "but", "his", "by", "from", "they", "we", "say", "her", "she",
    "or", "an", "will", "my", "one", "all", "would", "there", "their", "what",
    "people", "time", "year", "good", "some", "could", "them", "see", "other", "than",
    "then", "now", "look", "only", "come", "its", "over", "think", "also", "back",
    "after", "use", "two", "how", "our", "work", "first", "well", "way", "even",
    "new", "want", "because", "any", "these", "give", "day", "most", "us", "into",
    "education", "knowledge", "experience", "development", "important", "different",
    "understand", "question", "problem", "solution", "create", "change", "improve",
    "business", "project", "success", "growth", "quality", "value", "progress",
    "community", "health", "future", "environment", "culture", "history", "science",
    "research", "analysis", "strategy", "process", "management", "leadership", "challenge",
    "adventure", "beautiful", "celebrate", "demonstrate", "encourage", "favorite",
    "generous", "honest", "imagination", "justice", "knowledge", "language",
    "musician", "nature", "opportunity", "partnership", "question", "responsible",
    "strength", "teacher", "unique", "valuable", "welcome", "wisdom",
    "achievement", "brilliant", "courage", "discovery", "excellent", "faithful",
    "genuine", "happiness", "intelligent", "journey", "kindness", "learning",
    "meaningful", "negotiate", "obstacle", "patience", "quality", "resilient",
    "success", "trust", "universal", "volunteer", "wonderful", "yesterday",
    "zealous", "ability", "balance", "character", "determination", "effort"
  ];

  const [testStarted, setTestStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(testDurationInSeconds);
  const [currentWords, setCurrentWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentInput, setCurrentInput] = useState("");
  const [correctChars, setCorrectChars] = useState(0);
  const [incorrectChars, setIncorrectChars] = useState(0);
  const [completedWords, setCompletedWords] = useState(0);
  const [wpm, setWpm] = useState(null);
  const [accuracy, setAccuracy] = useState(100);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [typingHistory, setTypingHistory] = useState([]);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [testCompleted, setTestCompleted] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const generateWords = () => {
    const randomWords = [];
    for (let i = 0; i < 100; i++) {
      const randomIndex = Math.floor(Math.random() * wordList.length);
      randomWords.push(wordList[randomIndex]);
    }
    setCurrentWords(randomWords);
    setCurrentWordIndex(0);
    setCurrentCharIndex(0);
  };

  const processTypedWord = (typedWord) => {
    const currentWord = currentWords[currentWordIndex];
    let localCorrect = 0;
    let localIncorrect = 0;

    for (let i = 0; i < typedWord.length; i++) {
      if (i < currentWord.length && typedWord[i] === currentWord[i]) {
        localCorrect++;
      } else {
        localIncorrect++;
      }
    }

    if (typedWord.length < currentWord.length) {
      localIncorrect += currentWord.length - typedWord.length;
    }

    correctCharsRef.current += localCorrect;
    incorrectCharsRef.current += localIncorrect;
    totalTypedCharsRef.current += typedWord.length;
    
    setCorrectChars(correctCharsRef.current);
    setIncorrectChars(incorrectCharsRef.current);
    
    const total = correctCharsRef.current + incorrectCharsRef.current;
    if (total > 0) {
      setAccuracy(Math.round((correctCharsRef.current / total) * 100));
    }

    setTypingHistory(prev => [...prev, {
      word: currentWord,
      typed: typedWord,
      correct: localCorrect,
      incorrect: localIncorrect
    }]);

    setCurrentWordIndex(prevIndex => prevIndex + 1);
    setCurrentCharIndex(0);
    setCompletedWords(prev => prev + 1);

    if (currentWordIndex === currentWords.length - 1) {
      generateWords();
    }
  };

  useEffect(() => {
    const fetchTopResults = async () => {
      setLoading(true);
      try {
        const resultsRef = collection(db, "typingTestResults");
        const q = query(resultsRef, orderBy("wpm", "desc"));
        const querySnapshot = await getDocs(q);
        
        const userBestScores = new Map();
        
        querySnapshot.docs.forEach((doc) => {
          const result = { id: doc.id, ...doc.data() };
          const userId = result.uid;
          
          if (!userBestScores.has(userId) || userBestScores.get(userId).wpm < result.wpm) {
            userBestScores.set(userId, result);
          }
        });
        
        const topResults = Array.from(userBestScores.values())
          .sort((a, b) => b.wpm - a.wpm)
          .slice(0, 10);
        
        const resultsWithUsernames = await Promise.all(
          topResults.map(async (result) => {
            try {
              const userDocRef = doc(db, "users", result.uid);
              const userDocSnap = await getDoc(userDocRef);
              
              if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                return {
                  ...result,
                  username: userData.username || result.username
                };
              }
            } catch (fetchError) {
              console.error(`Error fetching user data for ${result.uid}:`, fetchError.message);
            }
            return result;
          })
        );
        
        setResults(resultsWithUsernames);
      } catch (error) {
        console.error("Error fetching leaderboard:", error.message);
      }
      setLoading(false);
    };
    fetchTopResults();
    generateWords();
  }, []);

  useEffect(() => {
    let countdownTimer;
    if (showCountdown && countdown > 0) {
      countdownTimer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (showCountdown && countdown === 0) {
      setShowCountdown(false);
      startTest();
    }
    return () => clearTimeout(countdownTimer);
  }, [showCountdown, countdown]);

  useEffect(() => {
    let timerId;
    if (testStarted && timeLeft > 0) {
      timerId = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerId);
            finishTest(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [testStarted]);

  useEffect(() => {
    if (testStarted && inputRef.current) {
      inputRef.current.focus();
    }
  }, [testStarted]);

  const startCountdown = () => {
    setShowCountdown(true);
    setCountdown(3);
    setTestCompleted(false);
    isTestCompletedRef.current = false;
  };

  const startTest = () => {
    generateWords();
    setTestStarted(true);
    setTimeLeft(testDurationInSeconds);
    setCurrentInput("");
    setCurrentWordIndex(0);
    setCurrentCharIndex(0);
    setCorrectChars(0);
    setIncorrectChars(0);
    setCompletedWords(0);
    setWpm(null);
    setAccuracy(100);
    setTypingHistory([]);
    setTestCompleted(false);
    
    correctCharsRef.current = 0;
    incorrectCharsRef.current = 0;
    totalTypedCharsRef.current = 0;
    isTestCompletedRef.current = false;
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (e) => {
    if (!testStarted || testCompleted) return;
    
    const value = e.target.value;
    const lastChar = value.slice(-1);

    setCurrentCharIndex(value.length);

    if (lastChar === " " && value.trim() !== "") {
      processTypedWord(value.trim());
      setCurrentInput("");
    } else {
      setCurrentInput(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
    }
  };

  const calculateWPM = (isTimeUp = false) => {
    const secondsUsed = isTimeUp ? testDurationInSeconds : Math.max(1, testDurationInSeconds - timeLeft);
    const minutesUsed = secondsUsed / 60;
    const wordsTyped = correctCharsRef.current / 5;
    return Math.min(300, Math.round(wordsTyped / minutesUsed));
  };

  const finishTest = async (isTimeUp = false) => {
    if (isTestCompletedRef.current) return;
    
    isTestCompletedRef.current = true;
    setTestCompleted(true);
    setTestStarted(false);
    
    if (currentInput.trim()) {
      processTypedWord(currentInput.trim());
      setCurrentInput("");
    }

    const calculatedWPM = calculateWPM(isTimeUp);
    setWpm(calculatedWPM);

    const totalChars = correctCharsRef.current + incorrectCharsRef.current;
    const finalAccuracy = totalChars > 0
      ? Math.round((correctCharsRef.current / totalChars) * 100)
      : 100;
    setAccuracy(finalAccuracy);

    const testSessionId = `${user?.uid || 'guest'}-${Date.now()}`;
    
    if (user?.uid) {
      try {
        let displayUsername = user.displayName || user.email || "Unknown User";
        
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            displayUsername = userData.username || user.displayName || user.email || "Unknown User";
          }
        } catch (fetchError) {
          console.error("Error fetching user data:", fetchError.message);
        }
        
        const resultRef = collection(db, "typingTestResults");
        
        await addDoc(resultRef, {
          uid: user.uid,
          username: displayUsername,
          wpm: calculatedWPM,
          accuracy: finalAccuracy,
          timestamp: Timestamp.now(),
          charsTyped: totalTypedCharsRef.current,
          wordsCompleted: completedWords,
          testSessionId
        });
        
        const q = query(resultRef, orderBy("wpm", "desc"));
        const querySnapshot = await getDocs(q);
        
        const userBestScores = new Map();
        
        querySnapshot.docs.forEach((doc) => {
          const result = { id: doc.id, ...doc.data() };
          const userId = result.uid;
          
          if (!userBestScores.has(userId) || userBestScores.get(userId).wpm < result.wpm) {
            userBestScores.set(userId, result);
          }
        });
        
        const topResults = Array.from(userBestScores.values())
          .sort((a, b) => b.wpm - a.wpm)
          .slice(0, 10);
        
        const resultsWithUsernames = await Promise.all(
          topResults.map(async (result) => {
            try {
              const userDocRef = doc(db, "users", result.uid);
              const userDocSnap = await getDoc(userDocRef);
              
              if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                return {
                  ...result,
                  username: userData.username || result.username
                };
              }
            } catch (fetchError) {
              console.error(`Error fetching user data for ${result.uid}:`, fetchError.message);
            }
            return result;
          })
        );
        
        setResults(resultsWithUsernames);
      } catch (error) {
        console.error("Error saving typing test result:", error.message);
      }
    } else {
      console.warn("User is not logged in; record not saved.");
    }

    setTimeLeft(0);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    alert("Pasting is disabled. Please type manually.");
  };

  const renderWords = () => {
    const wordsPerLine = 6;
    const linesToShow = 3;
    const totalWordsToShow = wordsPerLine * linesToShow;
    
    const currentPage = Math.floor(currentWordIndex / totalWordsToShow);
    const startIdx = currentPage * totalWordsToShow;
    const endIdx = startIdx + totalWordsToShow;

    return (
      <div className="word-container">
        {currentWords.slice(startIdx, endIdx).map((word, index) => {
          const actualIndex = index + startIdx;
          const isCurrent = actualIndex === currentWordIndex;
          const isPast = actualIndex < currentWordIndex;
          
          if (isCurrent) {
            return (
              <span key={actualIndex} className="word current">
                {word.split('').map((char, charIdx) => {
                  let charClass = "char";
                  
                  if (charIdx < currentInput.length) {
                    if (char === currentInput[charIdx]) {
                      charClass = "char correct";
                    } else {
                      charClass = "char incorrect";
                    }
                  } else if (charIdx === currentInput.length) {
                    charClass = "char cursor";
                  }
                  
                  return (
                    <span key={charIdx} className={charClass}>
                      {char}
                    </span>
                  );
                })}
              </span>
            );
          }
          
          return (
            <span
              key={actualIndex}
              className={`word ${isPast ? 'completed' : ''}`}
            >
              {word}
            </span>
          );
        })}
      </div>
    );
  };

  const getRealtimeWPM = () => {
    if (!testStarted || testCompleted) return wpm || 0;
    return calculateWPM(false);
  };

  const renderCountdownOverlay = () => {
    if (!showCountdown) return null;
    
    return (
      <div className="countdown-overlay">
        <div className="countdown-number">{countdown}</div>
      </div>
    );
  };

  const renderResultDetails = () => {
    if (testStarted || wpm === null) return null;
    
    const totalChars = correctCharsRef.current + incorrectCharsRef.current;
    const charsPerSecond = totalChars / testDurationInSeconds;
    
    return (
      <div className="result-section">
        <div className="result-card">
          <h3>Your Results</h3>
          
          <div className="results-grid">
            <div className="result-item">
              <div className="result-item-value">{wpm}</div>
              <div className="result-item-label">Words Per Minute</div>
            </div>
            
            <div className="result-item">
              <div className="result-item-value">{accuracy}%</div>
              <div className="result-item-label">Accuracy</div>
            </div>
            
            <div className="result-item">
              <div className="result-item-value">{completedWords}</div>
              <div className="result-item-label">Words Completed</div>
            </div>
            
            <div className="result-item">
              <div className="result-item-value">{charsPerSecond.toFixed(1)}</div>
              <div className="result-item-label">Characters Per Second</div>
            </div>
          </div>
          
          <div className="btn-group">
            <button className="btn btn-primary" onClick={startCountdown}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  const formatUsername = (fullUsername) => {
    if (!fullUsername) return "Unknown User";
    
    const parts = fullUsername.split(' ');
    
    if (parts.length === 1) return parts[0];
    
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1][0];
    
    return `${firstName} ${lastInitial}.`;
  };

  return (
    <div className="typing-test-container">
      {renderCountdownOverlay()}
      
      <div className="typing-test-header">
        <img className="typing-test-brand" src={studydenHorizontal} alt="StudyDen" />
        <h1>Typing Speed Test</h1>
        <p>Measure your typing speed, accuracy, and consistency.</p>
      </div>
      
      <div className="typing-test-content">
        <div className="typing-test-card">
          <h2>Test Your Skills</h2>
          
          <div className="timer-container">
            <div className="timer-display">{timeLeft > 0 ? timeLeft : "--"}</div>
            
            {testStarted && (
              <div className="progress-bar-container">
                <div 
                  className="progress-bar-fill"
                  style={{ width: `${(timeLeft / testDurationInSeconds) * 100}%` }}
                ></div>
              </div>
            )}
          </div>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">
                {getRealtimeWPM()}
              </div>
              <div className="stat-label">WPM</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value">{accuracy}%</div>
              <div className="stat-label">Accuracy</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value">{completedWords}</div>
              <div className="stat-label">Words</div>
            </div>
          </div>
          
          <div ref={wordContainerRef}>
            {renderWords()}
          </div>
          
          <input
            ref={inputRef}
            type="text"
            className="typing-input"
            value={currentInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={!testStarted || testCompleted}
            placeholder={testStarted ? "Type here..." : "Press Start to begin typing..."}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
          />
          
          <div className="btn-group">
            {!testStarted && timeLeft === testDurationInSeconds && !showCountdown && !testCompleted && (
              <button className="btn btn-primary" onClick={startCountdown}>
                Start Test
              </button>
            )}
            
            {renderResultDetails()}
          </div>
          
          <button 
            onClick={() => navigate("/dashboard")} 
            className="btn btn-secondary"
            style={{ width: "100%" }}
          >
            Back to Dashboard
          </button>
        </div>
        
        <div className="leaderboard-container">
          <h3 className="leaderboard-header">Top 10 Fastest Typists</h3>
          {loading ? (
            <div className="loading">Loading leaderboard...</div>
          ) : (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Username</th>
                  <th>WPM</th>
                  <th>Accuracy</th>
                  <th>Date/Time</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr 
                    key={result.id}
                    className={user?.uid === result.uid ? "user-row" : ""}
                  >
                    <td>
                      <span className="rank">#{index + 1}</span>
                    </td>
                    <td>{formatUsername(result.username)}</td>
                    <td>
                      <span className="wpm-value">{result.wpm}</span>
                    </td>
                    <td>{result.accuracy || "N/A"}%</td>
                    <td>
                      {result.timestamp
                        ? new Date(result.timestamp.seconds * 1000).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TypingTest;
